import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { ExportConfigView, ReportOption, RunView } from '../types'

const BASE = '/v1/exports'

export function useExportReports() {
  return useQuery({
    queryKey: ['exports', 'reports'],
    queryFn: async () => (await apiClient.get<ReportOption[]>(`${BASE}/reports`)).data,
    staleTime: 5 * 60_000,
  })
}

export function useExportConfigs() {
  return useQuery({
    queryKey: ['exports', 'configs'],
    queryFn: async () => (await apiClient.get<ExportConfigView[]>(BASE)).data,
  })
}

export interface SaveExportConfig {
  legalEntityId: string | null
  name: string
  reportKey: string
  format: string
  destination: string
  targetPath: string
  sftpHost: string | null
  sftpPort: number | null
  sftpUsername: string | null
  /** Omitted on an update leaves the stored password alone. */
  sftpPassword?: string
  sftpHostKey: string | null
  cron: string
  active: boolean
}

export function useExportMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['exports'] })

  return {
    create: useMutation({
      mutationFn: async (body: SaveExportConfig) =>
        (await apiClient.post<ExportConfigView>(BASE, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, body }: { id: string; body: SaveExportConfig }) =>
        (await apiClient.put<ExportConfigView>(`${BASE}/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => {
        await apiClient.delete(`${BASE}/${id}`)
      },
      onSuccess: invalidate,
    }),
    // Synchronous on the server too: after configuring an SFTP target the only useful
    // answer is whether the file arrived.
    runNow: useMutation({
      mutationFn: async (id: string) => (await apiClient.post<RunView>(`${BASE}/${id}/run`)).data,
      onSuccess: invalidate,
    }),
  }
}
